import { useEffect, useState } from "react";
import { ChevronRight, Sparkles, Loader2, Filter } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useData } from "@/context/DataContext";
import { METRICS, colLetter, fmtU, metricSeries } from "@/lib/metrics";
import { API } from "@/lib/api";

const findLine = (model, entity, key) => {
  const e = model?.entities.find((x) => x.code === entity);
  if (!e) return null;
  for (const [sec, lines] of Object.entries(e.sections)) {
    const l = lines.find((x) => x.key === key);
    if (l) return { e, l, sec };
  }
  return null;
};

const agg = (arr, yi, span, bal) => {
  if (yi < 0) return null;
  const s = arr.slice(yi, yi + span);
  return bal ? s[s.length - 1] : s.reduce((a, b) => a + (b || 0), 0);
};

const resolve = (payload, d) => {
  if (!payload) return null;
  const yi = payload.model.years.indexOf(d.y);
  if (yi < 0) return null;
  const span = d.span || 1;
  const bal = METRICS[d.metric]?.balance;
  if (d.lineKey) {
    const f = findLine(payload.model, d.entity, d.lineKey);
    return f ? { v: agg(f.l.values, yi, span), pv: yi - span >= 0 ? agg(f.l.values, yi - span, span) : null, f } : null;
  }
  const s = metricSeries(payload, d.metric, d.entity);
  return { v: agg(s, yi, span, bal), pv: yi - span >= 0 ? agg(s, yi - span, span, bal) : null };
};

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 border-b border-slate-800 py-1.5 text-sm" data-testid={testid}>
    <span className="text-slate-400">{k}</span><span className="num text-right text-slate-100">{v}</span>
  </div>
);

const useExplain = (ctxObj, key) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(""); }, [key]);
  const run = async () => {
    setBusy(true);
    setText("");
    try {
      const r = await fetch(`${API}/explain`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("srm_token")}` }, body: JSON.stringify({ context: ctxObj }) });
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        parts.forEach((p) => {
          const s = p.replace(/^data: /, "");
          if (s === "[DONE]") return;
          try {
            const j = JSON.parse(s);
            if (j.t) setText((t) => t + j.t);
            if (j.error) setText("AI explanation unavailable right now.");
          } catch { /* partial */ }
        });
      }
    } catch {
      setText("AI explanation unavailable right now.");
    } finally {
      setBusy(false);
    }
  };
  return { text, busy, run };
};

export const DetailPanel = () => {
  const { data, prev, detail, setDetail, setFilters, filters } = useData();
  const U = filters.unit === "lakh" ? "lakh" : "cr";
  const fx = (v, u) => (u === "lakh" ? fmtU(v * 100, "lakh") : fmtU(v, "cr"));
  const span = detail?.span || 1;
  const d = detail ? { ...detail, y: data.model.years[detail.yi] } : null;
  const fyLabel = d ? (span > 1 ? `FY ${d.y} – FY ${data.model.years[detail.yi + span - 1]} (${span}-yr total)` : `FY ${d.y}`) : "";
  const m = d ? METRICS[d.metric] || METRICS.income : null;
  const cur = d ? resolve(data, d) : null;
  const pv = d && prev ? resolve(prev, d) : null;
  const ent = d?.entity ? data.model.entities.find((e) => e.code === d.entity) : null;
  const line = cur?.f?.l;
  const yoy = cur && cur.pv ? ((cur.v - cur.pv) / Math.abs(cur.pv)) * 100 : null;
  const ref = line ? `${ent.sheet}!${colLetter(line.cols?.[d.yi] ?? 0)}${line.row}${line.formulas?.[d.yi] ? `  ${line.formulas[d.yi]}` : "  (input value)"}` : ent ? `Sheet "${ent.sheet}" › ${m?.label}: ${m?.ref}` : m?.ref;
  const assumptions = [];
  if (line?.escalation != null) assumptions.push(`Escalation ${(line.escalation * 100).toFixed(1)}% (${ent.basis?.[cur.f.sec] || "per workbook"})`);
  if (line?.basis_note) assumptions.push(`Basis: ${line.basis_note}`);
  if (ent) assumptions.push(`NCF switch = ${ent.switch}${ent.not_in_ncf ? " (not linked to NCF sheet)" : ""}`);
  if (ent && d.metric === "interest_expense") ent.loans.forEach((l) => assumptions.push(`${l.name} ${l.lender || ""} @ ${l.rate != null ? (l.rate * 100).toFixed(2) : "–"}%`));
  const label = line ? line.label : m?.label;
  const ctxObj = d && { financial_year: d.y, institution: ent?.name || "SRM Group (consolidated)", line_item: label, value_cr: cur?.v, previous_year_cr: cur?.pv,
    previous_version_cr: pv?.v ?? null, previous_version: prev?.version?.name || null, source: ref, assumptions };
  const ai = useExplain(ctxObj, JSON.stringify(detail));
  const drill = !d ? [] : !ent ? (m.e ? data.model.entities.filter((e) => e.switch).map((e) => ({ k: e.code, name: e.short, v: agg(m.e(e), d.yi, span, m.balance), go: () => setDetail({ ...detail, entity: e.code }) })) : [])
    : line ? [] : m.sections.flatMap((s) => ent.sections[s].filter((l) => Math.abs(agg(l.values, d.yi, span)) > 0.004).map((l) => ({ k: l.key, name: l.label, v: (m.outflow ? -1 : 1) * agg(l.values, d.yi, span), go: () => setDetail({ ...detail, lineKey: l.key }) })));
  return (
    <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
      <SheetContent className="w-full overflow-y-auto border-slate-800 bg-[#0f172a] sm:max-w-md" data-testid="detail-panel">
        {d && (
          <>
            <SheetHeader>
              <div className="flex flex-wrap items-center gap-1 text-[11px] text-slate-500" data-testid="detail-breadcrumb">
                <button onClick={() => setDetail({ ...detail, entity: null, lineKey: null })} className="hover:text-amber-400">Group</button>
                {ent && <><ChevronRight className="h-3 w-3" /><button onClick={() => setDetail({ ...detail, lineKey: null })} className="hover:text-amber-400">{ent.short}</button></>}
                {line && <><ChevronRight className="h-3 w-3" /><span>{data.section_labels[cur.f.sec] || cur.f.sec}</span><ChevronRight className="h-3 w-3" /><span>{line.label}</span></>}
              </div>
              <SheetTitle className="text-slate-50">{label}</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <Row k="Financial year" v={fyLabel} testid="detail-fy" />
              <Row k="Institution" v={ent?.name || "Group (consolidated)"} testid="detail-institution" />
              <Row k="Line item" v={label} />
              <Row k="Current value" v={fx((line && m.outflow ? -1 : 1) * cur?.v, U)} testid="detail-value" />
              <Row k={span > 1 ? "Previous period" : "Previous-year value"} v={cur?.pv != null ? fx((line && m.outflow ? -1 : 1) * cur.pv, U) : "–"} />
              <Row k={span > 1 ? "Period-on-period change" : "Year-on-year change"} v={yoy != null ? `${yoy >= 0 ? "+" : ""}${yoy.toFixed(1)}%` : "–"} testid="detail-yoy" />
              <Row k={`Previous version${prev ? ` (${prev.version.name})` : ""}`} v={pv ? fx((line && m.outflow ? -1 : 1) * pv.v, U) : "No earlier version"} testid="detail-prev-version" />
              <Row k="Version variance" v={pv ? fx((line && m.outflow ? -1 : 1) * (cur.v - pv.v), U) : "–"} />
            </div>
            <div className="mt-4 text-[11px] uppercase tracking-wider text-slate-500">Formula / source</div>
            <div className="num mt-1 break-all rounded bg-slate-900 p-2 text-xs text-amber-300" data-testid="detail-source">{ref}</div>
            <div className="mt-4 text-[11px] uppercase tracking-wider text-slate-500">Applicable assumptions</div>
            <ul className="mt-1 space-y-1 text-xs text-slate-300" data-testid="detail-assumptions">{assumptions.length ? assumptions.map((a) => <li key={a}>• {a}</li>) : <li className="text-slate-500">None recorded for this item</li>}</ul>
            {ent && <Button size="sm" variant="outline" className="mt-4 border-slate-700" data-testid="detail-filter-entity-button" onClick={() => setFilters((f) => ({ ...f, entity: ent.code }))}><Filter className="mr-1.5 h-3.5 w-3.5" />Filter dashboard to {ent.short}</Button>}
            {drill.length > 0 && (
              <>
                <div className="mt-5 text-[11px] uppercase tracking-wider text-slate-500">Drill down</div>
                <div className="mt-1" data-testid="detail-drill-list">
                  {drill.map((x) => (
                    <button key={x.k} onClick={x.go} className="flex w-full justify-between border-b border-slate-800 py-1.5 text-left text-sm text-slate-200 hover:text-amber-400" data-testid={`drill-${x.k}`}>
                      <span>{x.name}</span><span className="num">{fx(x.v, U)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="mt-5 rounded-lg border border-slate-800 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-300"><Sparkles className="h-3.5 w-3.5 text-amber-400" />AI explanation</span>
                <Button size="sm" variant="ghost" onClick={ai.run} disabled={ai.busy} data-testid="detail-ai-explain-button">{ai.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Explain"}</Button>
              </div>
              {ai.text && <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-slate-300" data-testid="detail-ai-text">{ai.text}</p>}
              <p className="mt-2 text-[10px] text-slate-500">Generated by Claude from the figures above only; verify against the workbook.</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
